import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import {
  AUDIENCE_LAYERS, CLUSTER_TYPES, META_DISCLAIMER,
  type InterestOutput, type InterestCluster, type InterestIdea, type DiscoveryInputs,
} from "@shared/interest.ts";
import { TestingMatrix } from "../components/interest/TestingMatrix.tsx";

const layerLabel = (id: string) => AUDIENCE_LAYERS.find((l) => l.id === id)?.label ?? id;
const clusterTypeLabel = (id: string) => CLUSTER_TYPES.find((l) => l.id === id)?.label ?? id;

const FIELDS: { key: keyof DiscoveryInputs; label: string; ph?: string; area?: boolean }[] = [
  { key: "product", label: "สินค้า/บริการ", ph: "เช่น รถ SUV มือสอง" },
  { key: "price", label: "ราคา", ph: "เช่น 5-8 แสน" },
  { key: "location", label: "พื้นที่ (Location)", ph: "เช่น กรุงเทพ+ปริมณฑล" },
  { key: "customerType", label: "ประเภทลูกค้า", ph: "เช่น ครอบครัวมีลูก" },
  { key: "age", label: "อายุ", ph: "เช่น 30-45" },
  { key: "profession", label: "อาชีพ", ph: "เช่น พนักงานออฟฟิศ, เจ้าของกิจการ" },
  { key: "lifestyle", label: "ไลฟ์สไตล์", ph: "เช่น ชอบเที่ยว ครอบครัว" },
  { key: "purchasingPower", label: "สมมติฐานกำลังซื้อ", ph: "เช่น ผ่อนไหว มีรถอยู่แล้ว" },
  { key: "objective", label: "วัตถุประสงค์แคมเปญ", ph: "เช่น Lead / Message / Traffic" },
  { key: "campaignType", label: "ประเภทแคมเปญ", ph: "เช่น เปิดตัว / เร่งปิดการขาย" },
  { key: "pain", label: "Pain", ph: "ความกังวลของลูกค้า", area: true },
  { key: "desire", label: "Desire", ph: "สิ่งที่ลูกค้าอยากได้", area: true },
];

export default function Interest() {
  const { activeBrand } = useBrands();
  const [inputs, setInputs] = useState<DiscoveryInputs>({});
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<InterestOutput | null>(null);
  const [audienceId, setAudienceId] = useState<string | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [history, setHistory] = useState<{ id: string; name: string; persona: string }[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [rawDebug, setRawDebug] = useState<string | null>(null);
  const [tab, setTab] = useState<"discover" | "matrix">("discover");
  const [savedLearn, setSavedLearn] = useState(false);

  useEffect(() => { loadHistory(); }, [activeBrand?.id]);

  async function loadHistory() {
    if (!activeBrand) { setHistory([]); return; }
    const r = await api.get<{ discoveries: any[] }>(`/interest?brandId=${activeBrand.id}`);
    setHistory(r.discoveries);
  }

  function set(k: keyof DiscoveryInputs, v: string) { setInputs((p) => ({ ...p, [k]: v })); }

  async function generate() {
    if (!activeBrand) { setErr("กรุณาเลือกแบรนด์ก่อน (มุมขวาบน)"); return; }
    setErr(null); setRawDebug(null); setLoading(true); setOutput(null); setSavedLearn(false);
    try {
      const r = await api.post<any>("/interest/generate", { brandId: activeBrand.id, inputs });
      if (r.error) {
        setErr("AI ตอบไม่เป็นรูปแบบที่อ่านได้ ลองใหม่อีกครั้ง");
        if (r.text) setRawDebug(String(r.text));
        return;
      }
      setOutput(r.output); setAudienceId(r.audienceId); setIsMock(r.isMock);
      loadHistory();
    } catch (e: any) {
      setErr("เกิดข้อผิดพลาด: " + (e?.message || "unknown"));
    } finally { setLoading(false); }
  }

  async function openDiscovery(id: string) {
    setLoading(true); setErr(null);
    try {
      const r = await api.get<{ discovery: any; clusters: any[]; ideas: any[] }>(`/interest/${id}`);
      setOutput(r.discovery.data as InterestOutput);
      setAudienceId(id); setIsMock(false); setTab("discover");
    } finally { setLoading(false); }
  }

  async function saveLearning() {
    if (!activeBrand || !output) return;
    await api.post("/learnings", {
      brandId: activeBrand.id, type: "interest",
      title: `Interest Discovery: ${output.persona.slice(0, 60)}`,
      situation: JSON.stringify(inputs), sourceModule: "interest", sourceId: audienceId,
      confidence: "medium",
    });
    setSavedLearn(true);
  }

  const byCluster = (ref: string) => output?.ideas.filter((i) => i.clusterRef === ref) ?? [];

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">Facebook Interest Intelligence</div>
          <div className="page__subtitle">
            {activeBrand ? <>บริบท: <b>{activeBrand.name}</b> — Audience Discovery Engine</> : "เลือกแบรนด์ก่อนเริ่ม"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className={"mh-btn mh-btn--sm " + (tab === "discover" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("discover")}>ค้นหา Audience</button>
          <button className={"mh-btn mh-btn--sm " + (tab === "matrix" ? "mh-btn--primary" : "mh-btn--ghost")} onClick={() => setTab("matrix")}>Testing Matrix</button>
        </div>
      </div>

      {tab === "matrix" ? (
        <TestingMatrix brandId={activeBrand?.id ?? null} clusters={output?.clusters ?? []} />
      ) : (
        <>
          {/* input form */}
          <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
            <div className="mh-spread" style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: 15 }}>ข้อมูลนำเข้า (Discovery Inputs)</h3>
              <span className="mh-muted" style={{ fontSize: 12.5 }}>ช่องว่างจะดึงจาก Brand Brain ให้อัตโนมัติ</span>
            </div>
            <div className="form-grid">
              {FIELDS.map((f) => (
                <div className="mh-field" style={{ margin: 0, gridColumn: f.area ? "1 / -1" : undefined }} key={f.key}>
                  <label className="mh-label">{f.label}</label>
                  {f.area
                    ? <textarea className="mh-textarea" style={{ minHeight: 64 }} value={(inputs[f.key] as string) || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.ph} />
                    : <input className="mh-input" value={(inputs[f.key] as string) || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.ph} />}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={generate} disabled={loading}>
                {loading ? <span className="spinner" /> : <><Icon name="Radar" size={18} /> สร้าง Audience & Interest</>}
              </button>
              {history.length > 0 && (
                <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && openDiscovery(e.target.value)}>
                  <option value="">📁 เปิดผลเดิม ({history.length})</option>
                  {history.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              )}
              {err && <span className="mh-pill mh-pill--problem">{err}</span>}
            </div>
          </div>

          {rawDebug && (
            <div className="mh-card card-pad" style={{ marginBottom: 16, borderColor: "var(--red-600)" }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>คำตอบดิบจาก AI (สำหรับ debug)</div>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, maxHeight: 240, overflow: "auto", background: "var(--gray-050)", padding: 10, borderRadius: 8 }}>{rawDebug.slice(0, 2000)}</pre>
            </div>
          )}

          {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังวิเคราะห์กลุ่มเป้าหมาย...</p></div>}

          {output && (
            <>
              {isMock && (
                <div className="mock-banner">
                  <Icon name="Wrench" size={18} />
                  <div>ผลนี้มาจาก <b>Mock AI</b> (โครงจริง) — ใส่ Claude API Key ในหน้าตั้งค่าเพื่อได้การวิเคราะห์จริง</div>
                </div>
              )}

              <div className="mh-card card-pad" style={{ marginBottom: 14, borderColor: "#f2c94c", background: "#fffdf5" }}>
                <div style={{ fontSize: 13.5, color: "#7a5900", lineHeight: 1.6 }}>{META_DISCLAIMER}</div>
              </div>

              <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
                <div className="mh-spread">
                  <div>
                    <div className="mh-sec" style={{ fontSize: 13 }}>Persona</div>
                    <div style={{ fontWeight: 600, marginTop: 3 }}>{output.persona}</div>
                  </div>
                  <button className="mh-btn mh-btn--subtle mh-btn--sm" onClick={saveLearning} disabled={savedLearn}>
                    {savedLearn ? "บันทึกแล้ว ✓" : <><Icon name="Save" size={15} /> Save as Learning</>}
                  </button>
                </div>
              </div>

              <details className="mh-card card-pad" style={{ marginBottom: 14, background: "var(--blue-050)", borderColor: "var(--blue-100)" }}>
                <summary style={{ cursor: "pointer", fontWeight: 700 }}>📖 วิธีอ่านผลนี้ (กดเพื่อดู)</summary>
                <div style={{ fontSize: 13.5, lineHeight: 1.8, marginTop: 10 }}>
                  <b>Cluster (กลุ่มก้อนใหญ่)</b> = แนวทางการหากลุ่มเป้าหมาย 1 แบบ เช่น "กลุ่มกำลังซื้อถึง" — ในแต่ละ Cluster มี:<br />
                  • <b>คำค้นใน Meta</b> = คำที่เอาไปพิมพ์หาใน Ads Manager ตอนตั้งกลุ่มเป้าหมาย (ต้องเช็คก่อนว่ามีจริง)<br />
                  • <b>งบแนะนำ</b> = ควรเริ่มยิงกลุ่มนี้วันละเท่าไหร่<br />
                  • <b>🛑 Kill Signal</b> = ถ้าเจอแบบนี้ให้หยุดกลุ่มนี้ · <b>📈 Scale Signal</b> = ถ้าเจอแบบนี้ให้เพิ่มงบ<br />
                  • <b>P1-P5</b> = ลำดับที่ควรทดสอบก่อน-หลัง (P1 = ลองก่อน) · <b>ความมั่นใจ</b> = high/medium/low ว่ากลุ่มนี้น่าจะเวิร์คแค่ไหน<br />
                  <span className="mh-muted">💡 เริ่มจากกลุ่ม P1 ที่ความมั่นใจสูง ลองยิงด้วยงบเล็กๆ ก่อน แล้วดู Kill/Scale Signal</span>
                </div>
              </details>

              {output.clusters.map((cl: InterestCluster) => (
                <div className="mh-card" style={{ marginBottom: 16, overflow: "hidden" }} key={cl.ref}>
                  <div style={{ padding: "14px 18px", background: "var(--navy-800)", color: "#fff" }}>
                    <div className="mh-spread">
                      <div style={{ fontWeight: 700, fontSize: 16 }}>{cl.name}</div>
                      <span className="mh-pill" style={{ background: "rgba(255,255,255,0.15)", color: "#fff" }}>{clusterTypeLabel(cl.type)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-on-navy-muted)", marginTop: 6 }}>{cl.hypothesis}</div>
                  </div>
                  <div className="card-pad">
                    {cl.metaInterests && cl.metaInterests.length > 0 && (
                      <div style={{ marginBottom: 14, background: "var(--blue-050)", borderRadius: 10, padding: 12 }}>
                        <div className="mh-spread" style={{ marginBottom: 8 }}>
                          <b style={{ fontSize: 13.5 }}>🎯 ชุด Interest พร้อมใช้ (ใส่ซ้อนใน 1 Ad Set)</b>
                          <button className="mh-btn mh-btn--subtle mh-btn--sm" onClick={() => navigator.clipboard?.writeText(cl.metaInterests!.join(", "))}>คัดลอกทั้งชุด</button>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {cl.metaInterests.map((kw, i) => (
                            <span key={i} style={{ background: "#fff", border: "1px solid var(--blue-100)", borderRadius: 999, padding: "4px 11px", fontSize: 13, cursor: "pointer" }} onClick={() => navigator.clipboard?.writeText(kw)} title="คลิกเพื่อคัดลอก">{kw}</span>
                          ))}
                        </div>
                        <div className="mh-muted" style={{ fontSize: 11.5, marginTop: 7 }}>⚠️ ตรวจใน Meta Ads Manager ก่อนว่ามีคำนี้จริง แล้วเลือกใส่ 5-10 คำที่ตรงที่สุดต่อ 1 Ad Set</div>
                      </div>
                    )}
                    <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", marginBottom: 14 }}>
                      <MiniKV k="งบแนะนำ" v={cl.budgetSuggestion} />
                      <MiniKV k="เป้าหมายเทสต์" v={cl.testObjective} />
                      <MiniKV k="KPI หลัก" v={cl.kpi} />
                      <MiniKV k="🛑 Kill Signal" v={cl.killSignal} />
                      <MiniKV k="📈 Scale Signal" v={cl.scaleSignal} />
                    </div>
                    <div style={{ display: "grid", gap: 12 }}>
                      {byCluster(cl.ref).map((idea: InterestIdea, i) => <IdeaCard idea={idea} key={i} />)}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

function MiniKV({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ background: "var(--gray-050)", borderRadius: 10, padding: "10px 12px" }}>
      <div className="mh-muted" style={{ fontSize: 11.5, marginBottom: 3 }}>{k}</div>
      <div style={{ fontSize: 13.5, fontWeight: 500 }}>{v}</div>
    </div>
  );
}

function IdeaCard({ idea }: { idea: InterestIdea }) {
  const conf = idea.confidence === "high" ? "mh-conf--high" : idea.confidence === "low" ? "mh-conf--low" : "mh-conf--medium";
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 14 }}>
      <div className="mh-spread" style={{ alignItems: "flex-start" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{idea.concept}</div>
          <div className="mh-muted" style={{ fontSize: 12 }}>{layerLabel(idea.layer)}</div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flex: "none" }}>
          <span className={"mh-conf " + conf}>{idea.confidence}</span>
          <span className="mh-pill mh-pill--neutral" title="ลำดับการทดสอบ">P{idea.testingPriority}</span>
        </div>
      </div>
      <div style={{ background: "var(--blue-050)", borderRadius: 8, padding: "7px 10px", margin: "10px 0", fontSize: 13 }}>
        🔎 คำค้นที่คาดว่าใช้ได้ใน Meta: <b>{idea.metaSearchTerm}</b> <span className="mh-muted">(ตรวจใน Ads Manager ก่อน)</span>
      </div>
      <div style={{ display: "grid", gap: 6, fontSize: 13.5 }}>
        <Line k="ทำไมกลุ่มนี้น่ายิง" v={idea.whyMatter} />
        <Line k="ไอเดีย Hook" v={idea.hook} />
        <Line k="⚠️ ความเสี่ยง" v={idea.risk} />
      </div>
    </div>
  );
}

function Line({ k, v }: { k: string; v: string }) {
  if (!v) return null;
  return <div><span className="mh-muted" style={{ fontSize: 12.5 }}>{k}: </span>{v}</div>;
}
